import React from "react";
import { ExternalLink, ArrowUpRight } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { getMl, fileUrl } from "@/lib/api";

const BRAND_FAMILY_TITLES = {
  en: { eyebrow: "Brand Family", title: "Our Sister Brands", subtitle: "Explore the broader Nakia ecosystem of trusted, certified brands serving complementary markets.", visit: "Visit Website" },
  tr: { eyebrow: "Marka Ailesi", title: "Kardeş Markalarımız", subtitle: "Tamamlayıcı pazarlara hizmet veren güvenilir, sertifikalı markalardan oluşan geniş Nakia ekosistemini keşfedin.", visit: "Web Sitesini Ziyaret Et" },
  id: { eyebrow: "Keluarga Brand", title: "Brand Saudara Kami", subtitle: "Jelajahi ekosistem Nakia yang lebih luas berisi brand-brand terpercaya dan tersertifikasi yang melayani pasar pelengkap.", visit: "Kunjungi Website" },
};

const BrandFamily = ({ brands }) => {
  const { lang } = useI18n();
  const T = BRAND_FAMILY_TITLES[lang] || BRAND_FAMILY_TITLES.en;

  const items = (brands || []).slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  if (items.length === 0) return null;

  return (
    <section className="py-24 sm:py-32 bg-[#F8FAFC]" data-testid="brand-family-section">
      <div className="container-nakia">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="h-px w-10 bg-[#D4AF37]" />
            <span className="text-eyebrow text-[#D4AF37]">{T.eyebrow}</span>
            <span className="h-px w-10 bg-[#D4AF37]" />
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#0A1128] leading-tight">
            {T.title}
          </h2>
          <p className="text-slate-600 mt-4">{T.subtitle}</p>
        </div>

        <div className={`grid gap-6 ${items.length === 1 ? "grid-cols-1 max-w-3xl mx-auto" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"}`}>
          {items.map((b) => (
            <a
              key={b.id || b.name}
              href={b.link}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`brand-family-card-${(b.name || "brand").toLowerCase().replace(/\s+/g, "-")}`}
              className="group card-light overflow-hidden flex flex-col"
            >
              <div className="aspect-[16/9] overflow-hidden bg-slate-100 relative">
                {b.image_url ? (
                  <img
                    src={fileUrl(b.image_url)}
                    alt={b.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-[#0A1128]">
                    <span className="font-display text-3xl text-[#D4AF37] tracking-[0.15em]">{b.name}</span>
                  </div>
                )}
                <div className="absolute top-4 right-4 w-10 h-10 bg-[#D4AF37] flex items-center justify-center rounded-sm shadow-lg group-hover:scale-110 transition-transform">
                  <ArrowUpRight size={18} className="text-[#0A1128]" />
                </div>
              </div>
              <div className="p-7 flex-1 flex flex-col">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-3">Partner Brand</div>
                <h3 className="font-display text-2xl text-[#0A1128] mb-2 group-hover:text-[#D4AF37] transition-colors">
                  {b.name}
                </h3>
                {getMl(b.tagline, lang) && (
                  <p className="text-sm text-slate-700 font-medium mb-3">{getMl(b.tagline, lang)}</p>
                )}
                {getMl(b.description, lang) && (
                  <p className="text-sm text-slate-600 line-clamp-3 mb-5">{getMl(b.description, lang)}</p>
                )}
                <div className="mt-auto inline-flex items-center gap-2 text-[#0A1128] text-xs uppercase tracking-[0.2em] font-semibold border-t border-slate-100 pt-5">
                  <ExternalLink size={14} className="text-[#D4AF37]" />
                  {T.visit}
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BrandFamily;
